import { flexRender, Table } from '@tanstack/react-table';
import {
  cn,
  getCommonColumnPinningStyles,
  getCommonColumnPinningStylesForHeader,
} from '../utils';
import { Button, Empty, Pagination, Spin } from 'antd';
import { ITableFilters } from '../types';

export interface ICommonTableProps<T, U> {
  table: Table<T>;
  total?: number;
  filters?: U & ITableFilters;
  setFilters?: React.Dispatch<React.SetStateAction<U>>;
  hidePagination?: boolean;
  fullScreen?: boolean;
  limitHeight?: boolean;
  wrapText?: boolean;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  emptyTitle?: string;
  emptyDescription?: string;
}

const CommonTable = <T, U>({
  table,
  total,
  filters,
  setFilters,
  hidePagination = false,
  limitHeight = false,
  wrapText = true,
  loading = false,
  error = false,
  onRetry,
  emptyTitle = 'No data yet',
  emptyDescription = 'Records will appear here when they are available.',
}: ICommonTableProps<T, U>) => {
  const handlePageChange = (currentPage: number, currentPageSize: number) => {
    setFilters((prev: U) => ({
      ...prev,
      page: currentPage,
      pageSize: currentPageSize,
    }));
  };

  return (
    <>
      <div className="flex flex-col gap-6 h-full justify-between">
        <div
          className={cn(
            'overflow-x-auto outline outline-[1.5px] outline-gray-100 border-gray-200 rounded h-full',
            limitHeight ? 'min-h-[70vh] max-h-[70vh]' : '',
          )}
        >
          {loading && (
            <div className="grid min-h-56 place-items-center bg-white">
              <Spin size="small" tip="Loading records..." />
            </div>
          )}

          {!loading && error && (
            <div className="grid min-h-56 place-items-center bg-white px-6 text-center">
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-semibold text-text-200">
                    Unable to load records
                  </p>
                  <p className="mt-1 text-xs text-text-500">
                    Check your connection and try again.
                  </p>
                </div>
                {onRetry && (
                  <Button size="small" onClick={onRetry}>
                    Retry
                  </Button>
                )}
              </div>
            </div>
          )}

          {!loading && !error && table && (
            <>
              <table className="w-full border-spacing-0 scroll-m-4">
                {table?.getRowModel()?.rows.length !== 0 && (
                  <>
                    <thead className="sticky top-0 m-0 z-10">
                      {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup?.id} className="bg-gray-50">
                          {headerGroup.headers?.map(header => (
                            <th
                              className={cn(
                                'font-medium px-4 py-3.5 text-xs text-text-500 text-left',
                                wrapText
                                  ? 'whitespace-normal'
                                  : 'whitespace-nowrap',
                              )}
                              key={header.id}
                              style={{
                                ...getCommonColumnPinningStylesForHeader(
                                  header.column,
                                  table,
                                ),
                              }}
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                  )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                  </>
                )}

                <tbody>
                  {table?.getRowModel()?.rows.map(row => (
                    <tr
                      key={row.id}
                      className="bg-white border-t-[1.5px] border-text-950 rounded"
                    >
                      {row.getVisibleCells().map(cell => (
                        <td
                          className={cn(
                            'text-text-50 font-normal text-xs px-4 py-3.5 text-left text-nowrap',
                            wrapText
                              ? 'whitespace-normal'
                              : 'whitespace-nowrap',
                          )}
                          key={cell.id}
                          style={{
                            ...getCommonColumnPinningStyles(cell.column, table),
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {!loading && !error && table?.getRowModel()?.rows.length === 0 && (
            <div className="grid min-h-56 place-items-center bg-white px-6">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <div>
                    <p className="text-sm font-semibold text-text-200">
                      {emptyTitle}
                    </p>
                    <p className="mt-1 text-xs text-text-500">
                      {emptyDescription}
                    </p>
                  </div>
                }
              />
            </div>
          )}
        </div>
        {!hidePagination && (
          <>
            <div>
              {!loading && !error && table?.getRowModel()?.rows.length > 0 && (
                <Pagination
                  size="small"
                  total={total}
                  pageSize={filters.pageSize}
                  current={filters.page}
                  pageSizeOptions={[25, 50, 100, 200]}
                  showSizeChanger
                  showTotal={total => `Total ${total} items`}
                  onChange={handlePageChange}
                  onShowSizeChange={handlePageChange}
                  className="flex gap-1 items-center"
                />
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};
export default CommonTable;
